import { IFaxCoversheet } from '@dataTypes/fax.type'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from '../errors/api-error'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_FAX')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type GenericResponseData = Record<string, unknown>

type CoversheetResponseData = { fax_coversheet?: IFaxCoversheet }
type CoversheetListResponseData = { fax_coversheets?: IFaxCoversheet[] }

const getHeaders = (dbName?: string): Record<string, string> =>
  dbName ? { ...headers, 'x-db-name': dbName } : headers

// ——— Coversheet ———
export const createCoversheet = async (
  data: IFaxCoversheet,
): Promise<ApiResponse<CoversheetResponseData>> => {
  try {
    const response = await axios.post<ApiResponse<CoversheetResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/coversheet`,
      data,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in createCoversheet:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAllCoversheets = async (
  dbName?: string,
): Promise<ApiResponse<CoversheetListResponseData>> => {
  try {
    const response = await axios.get<ApiResponse<CoversheetListResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/coversheet/all`,
      { headers: getHeaders(dbName) },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAllCoversheets:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getCoversheetById = async (
  id: string,
): Promise<ApiResponse<CoversheetResponseData>> => {
  try {
    const response = await axios.get<ApiResponse<CoversheetResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/coversheet/${id}`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getCoversheetById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateCoversheetById = async (
  fax_coversheet_id: string,
  data: Partial<IFaxCoversheet>,
): Promise<ApiResponse<CoversheetResponseData>> => {
  try {
    const response = await axios.put<ApiResponse<CoversheetResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/coversheet`,
      { fax_coversheet_id, data },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateCoversheetById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteCoversheetById = async (
  id: string,
): Promise<ApiResponse<GenericResponseData>> => {
  try {
    const response = await axios.delete<ApiResponse<GenericResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/coversheet/${id}`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteCoversheetById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
